import React, { Children } from 'react';
import { HabitType, HabitEntry, DailyLog } from '../types/centurion';
import { CheckCircle2, Circle, Droplets, Dumbbell, BookOpen, Brain, UtensilsCrossed, Wine } from 'lucide-react';
import { motion } from 'framer-motion';
interface HabitChecklistProps {
  log: DailyLog;
  onUpdate: (type: HabitType, value: number | boolean) => void;
  disabled?: boolean;
}
const HABIT_CONFIG: Record<HabitType, {
  label: string;
  icon: React.ElementType;
  unit: string;
  type: 'boolean' | 'number';
}> = {
  noSugar: {
    label: 'No Sugar',
    icon: UtensilsCrossed,
    unit: '',
    type: 'boolean'
  },
  noAlcohol: {
    label: 'No Alcohol',
    icon: Wine,
    unit: '',
    type: 'boolean'
  },
  protein: {
    label: 'Protein',
    icon: Dumbbell,
    unit: 'g',
    type: 'number'
  },
  water: {
    label: 'Water',
    icon: Droplets,
    unit: 'L',
    type: 'number'
  },
  workout1: {
    label: 'Workout 1',
    icon: Dumbbell,
    unit: 'min',
    type: 'number'
  },
  workout2: {
    label: 'Workout 2 (Outdoor)',
    icon: Dumbbell,
    unit: 'min',
    type: 'number'
  },
  reading: {
    label: 'Reading',
    icon: BookOpen,
    unit: 'pages',
    type: 'number'
  },
  skillWork: {
    label: 'Skill Work',
    icon: Brain,
    unit: 'min',
    type: 'number'
  }
};
export function HabitChecklist({
  log,
  onUpdate,
  disabled = false
}: HabitChecklistProps) {
  const habits = Object.values(log.habits);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <motion.div variants={container} initial="hidden" animate="show" className="space-y-3" data-id="element-160">
      {habits.map(habit => {
      const config = HABIT_CONFIG[habit.type];
      const Icon = config.icon;
      const isCompleted = habit.completed;
      return <motion.div key={habit.type} variants={item} className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${isCompleted ? 'bg-emerald-900/10 border-emerald-500/30' : 'bg-surface-200 border-surface-100 hover:border-surface-50'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-161">
            <div className="flex items-center gap-4" data-id="element-162">
              <button onClick={() => {
            if (disabled) return;
            if (config.type === 'boolean') {
              onUpdate(habit.type, !habit.value);
            } else {
              // Quick complete for number types if clicked on icon
              onUpdate(habit.type, habit.target);
            }
          }} disabled={disabled} className={`focus:outline-none transition-colors ${isCompleted ? 'text-emerald-500' : 'text-gray-500 hover:text-centurion-400'}`} data-id="element-163">
                {isCompleted ? <CheckCircle2 size={24} data-id="element-164" /> : <Circle size={24} data-id="element-165" />}
              </button>

              <div className="flex flex-col" data-id="element-166">
                <span className={`font-medium ${isCompleted ? 'text-emerald-50' : 'text-gray-200'}`} data-id="element-167">
                  {config.label}
                </span>
                <span className="text-xs text-gray-500 font-mono-num" data-id="element-168">
                  Target:{' '}
                  {config.type === 'boolean' ? 'Yes' : `${habit.target}${config.unit}`}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-169">
              {config.type === 'number' && <div className="flex items-center gap-2" data-id="element-170">
                  <input type="number" value={habit.value as number || ''} onChange={e => {
              const val = parseFloat(e.target.value) || 0;
              onUpdate(habit.type, val);
            }} disabled={disabled} className="w-20 bg-surface-300 border border-surface-100 rounded px-2 py-1 text-right font-mono-num text-sm focus:outline-none focus:border-centurion-500 focus:ring-1 focus:ring-centurion-500 disabled:opacity-50" placeholder="0" min="0" step={config.unit === 'L' ? '0.1' : '1'} data-id="element-171" />
                  <span className="text-sm text-gray-500 w-8" data-id="element-172">
                    {config.unit}
                  </span>
                </div>}
              {config.type === 'boolean' && <div className="w-28 flex justify-end" data-id="element-173">
                  <span className={`text-sm font-mono-num ${isCompleted ? 'text-emerald-500' : 'text-gray-500'}`} data-id="element-174">
                    {isCompleted ? 'DONE' : 'PENDING'}
                  </span>
                </div>}
            </div>
          </motion.div>;
    })}
    </motion.div>;
}